import { TypedDocumentNode, gql } from '@apollo/client';
import QueryString from 'qs';
import { FormattedMessage } from 'react-intl';
import { generatePath, useSearchParams } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayL, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { FOOTBALL_CRAFT_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { cellBackground } from '../styles';
import { EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem } from './__generated__/index.graphql';
import essenceOpening from './assets/essence-opening.png';

const enterAnimation = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`;

const StyledDialog = styled(Dialog)`
  --height: var(--100vh);
  @media ${tabletAndAbove} {
    --height: min(100vh - 8 * var(--unit), 812px);
  }
  ${cellBackground}
  height: var(--height);
  transform: scale(0);
  animation: ${enterAnimation} 0.8s ease-out forwards;
  & > * {
    overflow: hidden;
    background: transparent;
  }
`;

const InnerWrapper = styled(Vertical)`
  height: 100%;
`;
const ImgWrapper = styled(Vertical)`
  flex: 1;
`;

const Content = styled(Vertical).attrs({ gap: 4 })`
  padding: var(--quadruple-unit) var(--double-unit);
  bottom: var(--double-unit);
  @media ${tabletAndAbove} {
    position: static;
    justify-content: flex-end;
  }
`;
const StyledStarBall = styled(StarBall)`
  margin-top: -40px;
`;

const Badge = styled(Horizontal).attrs({ gap: 0, center: true })`
  border-radius: var(--double-unit);
  width: min-content;
  padding: 2px var(--unit);
  border: 1px solid #ffb800;
  background: linear-gradient(-25deg, #ffb8001a 50%, transparent 50%)
    padding-box;
  align-self: center;
  margin-top: -50px;
`;

const gradientText = css`
  background: linear-gradient(323deg, #ae5e13 14.3%, #dfad46 85.7%);
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
`;

const BadgeText = styled(LabelL)`
  ${gradientText}
`;

const Title = styled(DisplayL)`
  ${gradientText}
`;

const Img = styled.img`
  filter: drop-shadow(0px 0px 70px #ffc700);
`;

type Props = {
  open: boolean;
  onClose: () => void;
  shardSlotItem: EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem;
  disableOnClose?: boolean;
};

// TODO: get this from backend
export const LIMITED_TIER_5_THRESHOLD = 20;

export const EssenceOpeningOnboardingDialog = ({
  open,
  onClose,
  shardSlotItem,
  disableOnClose,
}: Props) => {
  const canCraft = shardSlotItem.quantity >= LIMITED_TIER_5_THRESHOLD;
  const [searchParams] = useSearchParams();
  return (
    <StyledDialog
      transparent
      open={open}
      onClose={disableOnClose ? undefined : onClose}
      maxWidth="xs"
    >
      <InnerWrapper>
        <ImgWrapper center>
          <Img src={essenceOpening} alt="" width={160} />
          <Badge>
            <BadgeText bold>{`x${shardSlotItem.quantity}`}</BadgeText>
          </Badge>
        </ImgWrapper>

        <Content gap={4}>
          <Vertical gap={1}>
            <LabelM>
              <FormattedMessage
                id="EssenceOpeningOnboardingDialog.subtitle"
                defaultMessage="You won"
              />
            </LabelM>
            <Title>
              <FormattedMessage
                id="EssenceOpeningOnboardingDialog.title"
                defaultMessage="Limited<br></br>Essence"
                values={{
                  br: () => <br />,
                }}
              />
              <StyledStarBall
                width={16}
                color={
                  <linearGradient id="starball" x1="1" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor="#ae5e13" />
                    <stop offset="100%" stopColor="#dfad46" />
                  </linearGradient>
                }
              />
            </Title>
            <LabelM>
              {canCraft ? (
                <FormattedMessage
                  id="CraftCardDialog.description2"
                  defaultMessage="You collected enough Essence to craft a powerful Limited Card in the Card Factory"
                />
              ) : (
                <FormattedMessage
                  id="EssenceOpeningOnboardingDialog.description"
                  defaultMessage="Accumulate enough Essence to craft powerful Limited Cards in the Card Factory."
                />
              )}
            </LabelM>
          </Vertical>
          {canCraft ? (
            <Button
              size="large"
              color="secondary"
              to={`${generatePath(FOOTBALL_CRAFT_RARITY, { rarity: 'limited' })}${QueryString.stringify(
                { redirectUrl: searchParams.get('redirectUrl') },
                {
                  addQueryPrefix: true,
                }
              )}`}
            >
              <FormattedMessage
                id="CraftCardDialog.cta"
                defaultMessage="Craft My Card"
              />
            </Button>
          ) : (
            <Button size="large" color="secondary" onClick={onClose}>
              <FormattedMessage
                id="EssenceOpeningOnboardingDialog.cta"
                defaultMessage="Got it"
              />
            </Button>
          )}
        </Content>
      </InnerWrapper>
    </StyledDialog>
  );
};

EssenceOpeningOnboardingDialog.fragments = {
  shardSlotItem: gql`
    fragment EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem on ProbabilisticBundleSlotCardShardItem {
      id
      quantity
    }
  ` as TypedDocumentNode<EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem>,
};
